import React, { useState } from "react";

const DeleteTeamConfirmation = ({
  isOpen,
  onClose,
  team,
  teamMembers,
  hasDocuments,
  userList,
  onConfirm
}) => {
  const [action, setAction] = useState("close"); // 'delete' or 'close'
  const [transferDocuments, setTransferDocuments] = useState(false);
  const [targetUserId, setTargetUserId] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleConfirm = async () => {
    // Validation
    const hasMembers = teamMembers && teamMembers.length > 0;

    if (action === "delete" && hasMembers) {
      setError(
        "Cannot delete team with members. Use 'Close' instead or remove all members first."
      );
      return;
    }

    if (action === "delete" && hasDocuments) {
      setError(
        "Cannot delete team with documents. Use 'Close' instead or transfer documents first."
      );
      return;
    }

    if (action === "close" && transferDocuments && !targetUserId) {
      setError("Please select a user to transfer documents to.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await onConfirm({
        action,
        transferDocuments: action === "close" ? transferDocuments : false,
        targetUserId: transferDocuments ? targetUserId : null
      });
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to delete/close team");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setAction("close");
    setTransferDocuments(false);
    setTargetUserId("");
    setError("");
    setIsConfirmed(false);
    onClose();
  };

  const hasMembers = teamMembers && teamMembers.length > 0;
  const canDelete = !hasMembers && !hasDocuments;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 9999 }}
    >
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
        <h3 className="text-lg font-semibold mb-4 text-red-600">
          Delete or Close Team
        </h3>

        <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
          <p className="text-sm text-yellow-800">
            <strong>Warning:</strong> This action requires confirmation. Please
            review the options carefully.
          </p>
        </div>

        <div className="mb-4">
          <p className="text-sm mb-2">
            <strong>Team:</strong> {team?.Name}
          </p>
          {hasMembers && (
            <p className="text-sm text-orange-600 mb-1">
              <strong>Members:</strong> {teamMembers.length} user(s) in this
              team
            </p>
          )}
          {hasDocuments && (
            <p className="text-sm text-orange-600 mb-1">
              <strong>Documents:</strong> This team has documents assigned
            </p>
          )}
        </div>

        {/* Action Selection */}
        <div className="space-y-3 mb-6">
          <h4 className="font-medium text-sm mb-2">Select Action:</h4>

          {/* Delete Option */}
          <label
            className={`flex items-start cursor-pointer p-4 border rounded ${
              !canDelete
                ? "bg-gray-100 cursor-not-allowed opacity-60"
                : "hover:bg-base-200"
            }`}
          >
            <input
              type="radio"
              name="action"
              value="delete"
              checked={action === "delete"}
              onChange={(e) => {
                setAction(e.target.value);
                setTransferDocuments(false);
                setTargetUserId("");
              }}
              className="op-radio mt-1 mr-3"
              disabled={!canDelete}
            />
            <div className="flex-1">
              <div className="font-medium text-sm text-red-600">
                Delete Team Permanently
              </div>
              <div className="text-xs text-base-content/70 mt-1">
                Completely removes the team from the system. Only available if
                the team has no members and no documents.
              </div>
              {hasMembers && (
                <div className="text-xs text-red-500 mt-1">
                  ✗ Cannot delete: Team has {teamMembers.length} member(s)
                </div>
              )}
              {hasDocuments && (
                <div className="text-xs text-red-500 mt-1">
                  ✗ Cannot delete: Team has documents assigned
                </div>
              )}
            </div>
          </label>

          {/* Close Option */}
          <label className="flex items-start cursor-pointer p-4 border rounded hover:bg-base-200">
            <input
              type="radio"
              name="action"
              value="close"
              checked={action === "close"}
              onChange={(e) => {
                setAction(e.target.value);
              }}
              className="op-radio mt-1 mr-3"
            />
            <div className="flex-1">
              <div className="font-medium text-sm text-orange-600">
                Close Team
              </div>
              <div className="text-xs text-base-content/70 mt-1">
                Marks the team as inactive. All users will be removed from this
                team. Documents can optionally be transferred to another user.
              </div>
            </div>
          </label>
        </div>

        {/* Document Transfer Options (only for Close action) */}
        {action === "close" && hasDocuments && (
          <div className="border-t pt-4 mb-4">
            <h4 className="font-medium text-sm mb-3">
              Document Ownership Options:
            </h4>

            <div className="space-y-3">
              {/* Option 1: Leave documents alone */}
              <label className="flex items-start cursor-pointer p-3 border rounded hover:bg-base-200">
                <input
                  type="radio"
                  name="transferOption"
                  checked={!transferDocuments}
                  onChange={() => {
                    setTransferDocuments(false);
                    setTargetUserId("");
                  }}
                  className="op-radio mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    Keep Current Ownership
                  </div>
                  <div className="text-xs text-base-content/70 mt-1">
                    Documents remain assigned to their current owners.
                    Administrators can manage them later.
                  </div>
                </div>
              </label>

              {/* Option 2: Transfer documents */}
              <label className="flex items-start cursor-pointer p-3 border rounded hover:bg-base-200">
                <input
                  type="radio"
                  name="transferOption"
                  checked={transferDocuments}
                  onChange={() => {
                    setTransferDocuments(true);
                  }}
                  className="op-radio mt-1 mr-3"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">
                    Transfer All Documents
                  </div>
                  <div className="text-xs text-base-content/70 mt-1">
                    Transfer all team documents to a specified user or admin.
                  </div>
                </div>
              </label>

              {transferDocuments && (
                <div className="ml-8">
                  <label className="block text-sm font-medium mb-2">
                    Transfer documents to:
                  </label>
                  <select
                    value={targetUserId}
                    onChange={(e) => setTargetUserId(e.target.value)}
                    className="op-select op-select-bordered w-full"
                    disabled={isLoading}
                  >
                    <option value="">Select user</option>
                    {userList.map((u) => (
                      <option key={u.objectId} value={u.UserId?.objectId}>
                        {u.Name} ({u.Email}) -{" "}
                        {u.UserRole?.replace("contracts_", "")}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Documents will be transferred to this user and moved to one
                    of their teams.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Member Removal Notice */}
        {action === "close" && hasMembers && (
          <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> All {teamMembers.length} member(s) will be
              removed from this team when it is closed.
            </p>
          </div>
        )}

        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        {/* Confirmation Checkbox */}
        <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              id="confirmDelete"
              checked={isConfirmed}
              onChange={(e) => setIsConfirmed(e.target.checked)}
              className="op-checkbox op-checkbox-error mt-1 mr-3"
              disabled={isLoading}
            />
            <label htmlFor="confirmDelete" className="text-sm text-red-700">
              I understand that{" "}
              {action === "delete"
                ? "this will permanently delete the team and cannot be undone"
                : `this will close the team, remove all ${
                    hasMembers ? teamMembers.length : 0
                  } member(s), and ${
                    transferDocuments
                      ? "transfer documents to the selected user"
                      : "leave documents with their current owners"
                  }`}
              .
            </label>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleClose}
            className="op-btn op-btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className={`op-btn ${
              action === "delete" ? "op-btn-error" : "op-btn-warning"
            }`}
            disabled={isLoading || !isConfirmed}
          >
            {isLoading
              ? "Processing..."
              : action === "delete"
                ? "Delete Team"
                : "Close Team"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteTeamConfirmation;
